import type { PopoverProps as MuiPopoverProps } from '@material-ui/core/Popover'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Children, isValidElement, cloneElement, useEffect, useMemo } from 'react'

import useKeyStepper, { KeyMappings } from '@src/lib/hooks/useKeyStepper'
import unwrapFragment from '@src/lib/unwrapFragment'
import type { PopoverProps } from '@ui/Popover'
import Popover from '@ui/Popover'

import Menu, { MenuItem } from './menu-v2'
import * as styles from './popover-menu.css'

/**
 * Legacy way of indicating the placement of the popover
 */
interface PopoverOrigin {
  vertical: 'top' | 'center' | 'bottom' | number
  horizontal: 'left' | 'center' | 'right' | number
}

interface PopoverMenuProps
  extends Omit<MuiPopoverProps, 'onClose'>,
    Pick<
      PopoverProps,
      | 'targetRef'
      | 'placement'
      | 'withUnderlay'
      | 'autoFocus'
      | 'containFocus'
      | 'onClose'
      | 'offset'
    > {
  minWidth?: number
  maxWidth?: number
  size?: 'small' | 'medium'
  disableKeyStepper?: boolean
}

/**
 * @deprecated
 */
const PopoverMenu = function ({
  minWidth,
  maxWidth,
  children: childrenProp,
  size,
  disableKeyStepper = false,
  open,
  classes: classesProp,
  style,
  targetRef,
  onClose,
  placement,
  className,
  withUnderlay = true,
  containFocus = true,
  autoFocus = true,
  offset,
  anchorOrigin,
}: PopoverMenuProps) {
  const validatedChildren = useMemo(() => {
    const children = Children.toArray(childrenProp)
    const unwrappedChildren = children.map(unwrapFragment).flat()

    return (
      unwrappedChildren.map((child, index) => {
        if (!isValidElement(child)) {
          return <></>
        }

        return cloneElement(child, { key: child.key ?? index })
      }) ?? []
    )
  }, [childrenProp])

  const { selectedIndex, getItemProps, setSelectedIndex } = useKeyStepper({
    name: 'PopoverMenu',
    items: validatedChildren,
    keys: KeyMappings.VERTICAL,
    defaultSelectedIndex: -1,
    filter: () => open && !disableKeyStepper && !!validatedChildren?.some(isMenuItem),
    skip: (element) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- FIXME: Fix this ESLint violation!
      element != null && (!isMenuItem(element) || element.props?.['disabled']),
    handleSelect: (element, _, event) => {
      if (!element) {
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- FIXME: Fix this ESLint violation!
      element.props?.['onClick']?.(event)
    },
  })

  useEffect(() => {
    if (!open) {
      setSelectedIndex(null)
    }
  }, [open, setSelectedIndex])

  const children = disableKeyStepper
    ? childrenProp
    : validatedChildren?.map((child, index) => {
        const newProps: Record<string, unknown> = {}

        if (index === selectedIndex) {
          newProps.highlighted = true
        }

        return cloneElement(child, {
          ...newProps,
          key: child.key ?? index,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
          ...getItemProps(index, child.props),
        })
      })

  if (!open) {
    return null
  }

  return (
    <Popover
      targetRef={targetRef}
      isDismissable
      onClose={onClose}
      className={classNames(styles.root, className, classesProp)}
      placement={placement ?? transpileAnchorOriginToPlacement(anchorOrigin)}
      withUnderlay={withUnderlay}
      autoFocus={autoFocus}
      containFocus={containFocus}
      offset={offset}
      style={{
        ...style,
        minWidth,
        maxWidth,
      }}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
      <Menu size={size}>{children}</Menu>
    </Popover>
  )
}

function isMenuItem(element: JSX.Element) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-deprecated -- FIXME: Fix this ESLint violation!
  return element.type === MenuItem || element.type.actsAsMenuItem
}

function transpileAnchorOriginToPlacement(anchorOrigin?: PopoverOrigin) {
  if (!anchorOrigin) {
    return undefined
  }

  const yAxis =
    anchorOrigin.vertical === 'center' || typeof anchorOrigin.vertical === 'number'
      ? ''
      : anchorOrigin.vertical
  const xAxis =
    anchorOrigin.horizontal === 'center' || typeof anchorOrigin.horizontal === 'number'
      ? ''
      : anchorOrigin.horizontal

  if (xAxis && yAxis) {
    return `${yAxis} ${xAxis}` as const
  }

  if (yAxis) {
    return yAxis
  }

  if (xAxis) {
    return xAxis
  }

  return undefined
}

/**
 * @deprecated
 */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export default observer(PopoverMenu)
